import type { MonitorZustand } from "@kundeninfo/api-client";

/**
 * Die Anzeigeregeln der Monitor-Mandantensicht (Spec vom 08.09.2026) — Sprungadresse,
 * Rangfolge, Beschriftung und Status-Art.
 *
 * Sie stehen in einem EIGENEN Modul und nicht in der Kachel, weil sie zwei Ansichten
 * bedienen: den Verweis „Im Monitor ansehen" an den Portal-Mandanten in den Stammdaten
 * (Stufe A) und die Monitor-Kachel der Übersicht (Stufe B, customerOverview/MonitorTile.tsx).
 * Zwei Fassungen derselben Adresse wären genau die Sorte Doppelung, bei der die eine Ansicht
 * eines Tages ein `#mandant=` schreibt und die andere ein `#tenant=`.
 */

/**
 * Die Sprungadresse in die Mandantensicht des Monitors —
 * `<basis>/#mandant=<externeId>`, oder `null`.
 *
 * `null` heisst: An diesem System ist kein Monitor eingerichtet, und dann gibt es KEINEN
 * Verweis (Spec: „ohne Konfiguration kein Verweis, kein fest verdrahteter Host"). Die
 * Basis kommt vom Server (`monitorBasisUrl` an der Liste der Portal-Mandanten); in dieser
 * Oberfläche steht keine Adresse.
 *
 * Die ANMELDUNG macht der Monitor selbst: Er verlangt Keycloak (Realm `standard-energie`,
 * Rolle `app:monitor`), und wer im Kundeninfo angemeldet ist, kommt per SSO durch. Hier
 * wird deshalb nichts mitgegeben — kein Token, keine Kennung, kein Kundenname.
 *
 * `encodeURIComponent` auf der `externeId`, obwohl sie beim Anlegen gegen
 * `^[A-Za-z0-9_-]{1,40}$` geprüft wurde: Der Riegel steht im Schreibweg, diese Funktion
 * bekommt aber, was auch immer in der Datenbank steht — und eine Adresse, die aus einem
 * Datenfeld zusammengesetzt wird, kodiert ihre Teile. Beides zusammen ist Gürtel und
 * Hosenträger und kostet nichts.
 */
export function monitorSprungUrl(
  basisUrl: string | null | undefined, externeId: string,
): string | null {
  if (basisUrl === null || basisUrl === undefined) return null;
  const basis = basisUrl.trim().replace(/\/+$/, "");
  if (basis === "") return null;
  return `${basis}/#mandant=${encodeURIComponent(externeId)}`;
}

/**
 * Die RANGFOLGE der Zustände: ERROR > UNKNOWN > WARN > OK (Spec, und die Hausregel
 * „Schlechtestes oben").
 *
 * Kleinere Zahl heisst schlechter und damit weiter oben. `UNKNOWN` steht ausdrücklich VOR
 * `WARN` und weit vor `OK`: Was der Monitor nicht weiss, ist nicht in Ordnung — es ist
 * ungeprüft, und ungeprüft ist nie grün. Das ist der Punkt, an dem die meisten
 * Statusanzeigen falsch abbiegen.
 */
export const ZUSTAND_RANG: Record<MonitorZustand, number> = {
  ERROR: 0,
  UNKNOWN: 1,
  WARN: 2,
  OK: 3,
};

/** Die deutsche Beschriftung eines Zustands. VOLLSTÄNDIGE Zuordnung statt eines
 *  Rückfalls: Käme ein fünfter Zustand, meldete das der Typprüfer, statt eine rohe
 *  Konstante auf den Bildschirm zu lassen (Muster ZUSTAND_LABEL in Schnittstellen.tsx). */
export const ZUSTAND_LABEL: Record<MonitorZustand, string> = {
  OK: "In Ordnung",
  WARN: "Auffällig",
  ERROR: "Gestört",
  UNKNOWN: "Unbekannt",
};

/** Die Status-Art je Zustand für Chip und Listenkachel (me-design `status-art`). `UNKNOWN` ist grau, nie grün (Hausregel). */
export const ZUSTAND_STATUS_ART: Record<MonitorZustand, "ok" | "warnung" | "fehler" | "unbekannt"> = {
  OK: "ok", WARN: "warnung", ERROR: "fehler", UNKNOWN: "unbekannt",
};
